import React, { useEffect, useRef, useState, useContext } from "react";

import { WeeklyContext } from "../../../context/global/WeeklyContext";
import useOutsideClick from "../../../hooks/useOutsideClick";

import { getIsLastDay } from "../../../utils/datetime";

import { IoEllipsisHorizontal, IoAdd } from "react-icons/io5";
import { FiCopy } from "react-icons/fi";
import {
  FaTruckLoading,
  FaRegTrashAlt,
  FaRegCheckCircle,
  FaRegQuestionCircle,
} from "react-icons/fa";
import { PiToolbox } from "react-icons/pi";
import { GoPencil } from "react-icons/go";
import { GiMineTruck } from "react-icons/gi";
import { GrUserWorker } from "react-icons/gr";
import { RiRobot2Line } from "react-icons/ri";
import { MdOutlineMessage } from "react-icons/md";
import { AiOutlineStop } from "react-icons/ai";
import { DotsVertical, Duplicate, ViewList, ChevronDoubleRight, XCircle, LockOpen, LockClosed } from "../../icons/svg/outline";

import MessageModal from "../modals/MessageModal";

/**
 * DropDownList component renders a dropdown menu with a list of items. Each item has a label and an optional icon.
 * The items are clickable and trigger the respective click handler. Icons are derived from the first word of the item label.
 * Please add more icons to the icons object if needed.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.listItems - An dict object where keys are item labels and values are click handlers for the items.
 * @param {Array<string>} [props.disabledItems] - Array of item labels that should be disabled (greyed out).
 * @param {boolean} [props.icons=false] - A flag to indicate if icons should be displayed (currently unused).
 * @param {string} [props.className] - Additional class names for styling the dropdown.
 * @param {Date} [props.date] - The date object or string used for weekend menu positioning.
 * @param {string} [props.buttonIcon] - "ellipsis" (horizontal), "dotsVertical" (vertical), or "plus" icon for the dropdown button.
 * @param {string} [props.textOverride] - Text override for the menu text (overrides key).
 * @param {string} [props.align] - Menu alignment: "left" or "right" (defaults to auto based on isLastDay).
 * @param {string} [props.buttonClassName] - Additional classes for the button icon.
 * @param {number|string} [props.openKey] - Changing value opens the menu, for context-menu triggers.
 * @param {boolean} [props.showButton=true] - Render the menu button; false allows invisible anchored menus.
 *
 * @returns {JSX.Element} The rendered DropDownList component.
 */
const DropDownList = ({
  listItems,
  icons = true,
  className,
  date,
  buttonIcon,
  textOverride,
  textSuffix,
  align,
  buttonClassName = "",
  disabledItems = [],
  openKey,
  showButton = true,
}) => {
  // Menu state and logic, useref is for outside click close
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const weeklyContext = useContext(WeeklyContext);
  const showWeekends = weeklyContext?.showWeekends || false;
  const menuRef = useRef(null);
  /**
   * Toggles the dropdown menu open or closed.
   *
   * @returns {void}
   */
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };
  const isLastDay = getIsLastDay(date, showWeekends);

  /** Allow callers to open this menu from another gesture, such as right-click. */
  useEffect(() => {
    if (openKey != null) {
      setIsMenuOpen(true);
    }
  }, [openKey]);

  // Outside click should close menu
  useOutsideClick(menuRef, () => setIsMenuOpen(false));

  // Concise click handler for either opening the menu or clicking an item
  /**
   * Runs an item handler, or opens MessageModal when the handler is not a function.
   *
   * @param {Function|*} onClick Item click handler or modal trigger.
   * @returns {void}
   */
  const handleItemClick = (onClick) => {
    if (typeof onClick === "function") {
      onClick();
    } else {
      setIsModalOpen(true);
    }
    setIsMenuOpen(false);
  };

  if (!listItems) return null;

  // To prevent list from overflowing on the right side of the screen on final day
  // Use align prop if provided, otherwise use isLastDay logic
  var menuStyle = "left-0 origin-top-left";
  if (align === "right") {
    menuStyle = "right-0 origin-top-right";
  } else if (align === "left") {
    menuStyle = "left-0 origin-top-left";
  } else if (isLastDay) {
    menuStyle = "right-0 origin-top-right";
  }

  // Icon selection based off of the first word of the item label
  const iconType = {
    Copy: <FiCopy />,
    Paste: <FiCopy />,
    Clone: <Duplicate className="w-4 h-4" />,
    Delete: <FaRegTrashAlt />,
    Clear: <FaRegTrashAlt />,
    Set: <GoPencil />,
    Add: <IoAdd />,
    Edit: <GoPencil />,
    Concrete: <GiMineTruck />,
    Equipment: <PiToolbox />,
    Subcontractor: <GrUserWorker />,
    Dumping: <FaTruckLoading />,
    DeleteAll: <FaRegTrashAlt />,
    Complete: <FaRegCheckCircle />,
    Auto: <RiRobot2Line />,
    Smart: <RiRobot2Line />,
    Send: <MdOutlineMessage />,
    Cancel: <AiOutlineStop />,
    Test: <FaRegQuestionCircle />,
    Broadcast: <MdOutlineMessage />,
    Lock: <LockClosed className="w-4 h-4" aria-hidden />,
    Unlock: <LockOpen className="w-4 h-4" aria-hidden />,
  };

  const iconOverride = {
    "Set as Skipped": <ChevronDoubleRight className="w-4 h-4" />,
    "Remove Skipped": <XCircle className="w-4 h-4" />,
  };

  const buttonIcons = {
    ellipsis: <IoEllipsisHorizontal />,
    dotsVertical: <DotsVertical className="w-5 h-5" />,
    plus: <IoAdd size={20} />,
  };

  return (
    <div className={`relative text-base ${className}`} ref={menuRef}>
      {showButton ? (
        <button className={`my-auto align-middle ${buttonClassName}`} onClick={toggleMenu}>
          {buttonIcons[buttonIcon]}
        </button>
      ) : null}
      <div
        className={`absolute z-30 mt-2 w-auto bg-white border border-gray-300 shadow-lg transition-transform transform ${isMenuOpen ? "scale-100" : "scale-0"} transition-all ease-in-out ${menuStyle}`}
      >
        <ul className="text-left">
          {Object.entries(listItems).map(([text, onClick], index) => {
            const firstWord = text.split(" ")[0];
            const IconComponent =
              iconOverride[text] ?? iconType[firstWord] ?? (
                <ViewList className="w-4 h-4" aria-hidden />
              );
            const isDisabled = disabledItems.includes(text);
            const textColorClass =
              firstWord === "Delete" || firstWord === "Clear"
                ? isDisabled
                  ? "text-red-300"
                  : "text-red-500 hover:bg-red-200"
                : isDisabled
                  ? "text-gray-400"
                  : "";
            const textHoverClass =
              isDisabled
                ? ""
                : firstWord === "Delete"
                  ? "hover:bg-red-100"
                  : firstWord === "Complete"
                    ? "hover:bg-green-100"
                    : "hover:bg-gray-300";
            return (
              <li
                key={index}
                className={`${textColorClass} ${textHoverClass} m-auto px-1 ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  } text-nowrap flex items-center`}
                onClick={() => !isDisabled && handleItemClick(onClick)}
              >
                {IconComponent && icons && (
                  <span className="ml-1">{IconComponent}</span>
                )}
                <p className="ml-2 mr-1 my-[2px]">
                  {textOverride ? textOverride : text} {textSuffix}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
      <MessageModal
        isOpen={isModalOpen}
        title={"Feature Coming Soon"}
        message={"This feature is planned and will be available soon."}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default DropDownList;
